import { ReactNode } from "react";

import { css, cssMap } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs,
    Adg4TabPanel
} from "@/components/adg4/tabs/src";
import { Picture } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { LinkType } from "@/libraries/design/components/link/src/link";
import {
    DataEventProps,
    ProductKeyTypes,
    Row,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";

type BgColorType = "blue" | "black" | "white";

export interface PatternADG4TestimonialProps {
    bgColor?: BgColorType;
    heading?: string;
    subheading?: string;
    cta?: {
        text: string;
        url: string;
        dataEventProps?: DataEventProps;
    };
    testimonials: TestimonialsTypes[];
    productKey?: ProductKeyTypes;
    className?: string;
    imagePosition?: "left" | "right";
    testimonialCta?: ReactNode;
}

export type TestimonialsTypes = {
    eyebrow?: string;
    logo: JSX.Element;
    name: string;
    title: string;
    image: string;
    quote: string;
    link?: {
        text: string;
        url: string;
        type?: LinkType;
        dataEventProps?: DataEventProps;
    };
    label: string;
};

// @ts-ignore
const AUTO_TRANSITION_INTERVAL = 8000;
/* eslint-disable local/ensure-wac-token-usage */
// Not able to use wacToken function with cssMap. Need to refactor this in the future to use dynamic functions
const backgroundColorStyleMap = cssMap({
    blue: { background: "#1868DB" },
    black: { background: "#000" },
    white: { background: "#fff" }
});

const backgroundShapeStyleMap = cssMap({
    "image-left": {
        "clipPath": "polygon(2% 16%, 100% 0, 100% 100%, 0% 100%)",
        // hardcoded media query used since cssMap can't use media primitive
        "@media": {
            "not all and (min-width: 48rem)": {
                clipPath: "polygon(0 10%, 100% 0, 100% 100%, 0% 100%)"
            }
        }
    },
    "image-right": {
        "clipPath": "polygon(0 0, 100% 15%, 100% 100%, 0% 100%)",
        // hardcoded media query used since cssMap can't use media primitive
        "@media": {
            "not all and (min-width: 48rem)": {
                clipPath: "polygon(0 0, 100% 15%, 100% 100%, 0% 100%)"
            }
        }
    }
});

const customerImagesSectionStyleMap = cssMap({
    "image-left": {
        gridColumnStart: "unset"
    },
    "image-right": {
        "@media": {
            "(min-width: 64rem)": {
                gridColumnStart: "2"
            }
        }
    }
});

const customerQuotesSectionStyleMap = cssMap({
    "image-left": {
        gridColumnStart: "unset"
    },
    "image-right": {
        "@media": {
            "(min-width: 64rem)": {
                gridColumnStart: "1",
                gridRowStart: "1"
            }
        }
    }
});

const fontColorStyleMap = cssMap({
    "dark-bg": { color: "#fff !important" },
    "white-bg": { color: "unset" }
});

const topLineStyleMap = cssMap({
    blue: { backgroundColor: "#add8e6" },
    black: { backgroundColor: "#fff" },
    white: { backgroundColor: "#000" }
});

const logoOptionStyleMap = cssMap({
    "dark-bg": {
        svg: {
            path: {
                fill: "#fff"
            }
        },
        selectors: {
            "&[aria-selected=true]": {
                ".top-marker": {
                    backgroundColor: "#fff"
                }
            }
        }
    },
    "white-bg": {
        svg: {
            path: {
                fill: "#000"
            }
        },
        selectors: {
            "&[aria-selected=true]": {
                ".top-marker": {
                    backgroundColor: "#000"
                }
            }
        }
    }
});

// eslint-disable-next-line complexity
export const ADG4CustomerTestimonials = ({
    bgColor = "blue",
    heading,
    subheading,
    cta,
    testimonials,
    productKey,
    className,
    imagePosition = "left",
    testimonialCta
}: PatternADG4TestimonialProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    const logosContainerStyles = css({
        "display": testimonialCta ? "flex" : "block",
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".logos": {
            "margin-left": testimonialCta ? "0" : "auto"
        }
    });

    const bgTheme =
        bgColor === "blue" || bgColor === "black" ? "dark-bg" : "white-bg";

    return (
        <Adg4Tabs loop={true}>
            <TypographyStyles>
                <section
                    ref={scrollRef}
                    className={sectionIsVisible ? "is-visible" : ""}
                >
                    {!heading && !subheading && !cta ? null : (
                        <Row layout="8">
                            <div css={headerStyles}>
                                {heading && (
                                    <h2 css={h2Styles} className="">
                                        {heading}
                                    </h2>
                                )}
                                {subheading && (
                                    <p css={pStyles} className="body-lg">
                                        {subheading}
                                    </p>
                                )}
                                {cta && (
                                    <Link
                                        text={cta.text}
                                        href={cta.url}
                                        dataEventProps={cta.dataEventProps}
                                        type={"primary"}
                                    />
                                )}
                            </div>
                        </Row>
                    )}

                    <div
                        css={[
                            testimonialKeyframeStyles,
                            testimonialStyles,
                            fontColorStyleMap[bgTheme]
                        ]}
                        ref={scrollRef}
                        className={`${className} ${
                            sectionIsVisible ? "is-visible" : ""
                        } image-${imagePosition} ${bgColor}-background`}
                    >
                        <Row>
                            <div>
                                <div
                                    css={[
                                        bgShapeStyle,
                                        backgroundColorStyleMap[bgColor],
                                        backgroundShapeStyleMap[
                                            `image-${imagePosition}`
                                        ]
                                    ]}
                                    className="bgshape"
                                />
                                <div css={tabPanelWrapperStyles}>
                                    {testimonials.map((testimonial, index) => {
                                        return (
                                            <Adg4TabPanel
                                                id={`${index}`}
                                                panelIndex={index}
                                                key={index}
                                                forceRender={true}
                                            >
                                                <TopRowContent
                                                    testimonial={testimonial}
                                                    imagePosition={
                                                        imagePosition
                                                    }
                                                    index={index}
                                                    bgTheme={bgTheme}
                                                    bgColor={bgColor}
                                                />
                                            </Adg4TabPanel>
                                        );
                                    })}
                                </div>

                                <div css={rowWidthStyle} className="row-width">
                                    <div
                                        css={[
                                            logosContainerBaseStyle,
                                            logosContainerStyles
                                        ]}
                                        className="logos-container"
                                    >
                                        <Adg4DraggableTabList
                                            css={logosStyle}
                                            className="logos"
                                            rowGap="32px"
                                        >
                                            <div
                                                css={[
                                                    topLineStyles,
                                                    topLineStyleMap[bgColor]
                                                ]}
                                                className="top-line"
                                            />
                                            {testimonials.map(
                                                (testimonial, index) => {
                                                    return (
                                                        <Adg4DraggableTab
                                                            as="button"
                                                            css={[
                                                                logoOptionStyleMap[
                                                                    bgTheme
                                                                ],
                                                                logoOptionStyle
                                                            ]}
                                                            className="logo-option"
                                                            key={index}
                                                            tabIndex={index}
                                                            dataEventProps={{
                                                                "data-product-key":
                                                                    productKey,
                                                                "data-event-component":
                                                                    "tab",
                                                                "data-event-container":
                                                                    "testimonialsSection",
                                                                "data-event":
                                                                    "clicked",
                                                                "data-label-english":
                                                                    testimonial.label
                                                            }}
                                                            label={
                                                                testimonial.label
                                                            }
                                                        >
                                                            <LogoTabContent
                                                                testimonialLogo={
                                                                    testimonial.logo
                                                                }
                                                            />
                                                        </Adg4DraggableTab>
                                                    );
                                                }
                                            )}
                                        </Adg4DraggableTabList>
                                        {testimonialCta && testimonialCta}
                                    </div>
                                </div>
                            </div>
                        </Row>
                    </div>
                </section>
            </TypographyStyles>
        </Adg4Tabs>
    );
};

interface TopRowContentInterface {
    testimonial: TestimonialsTypes;
    imagePosition: "left" | "right";
    index: number;
    bgTheme: "dark-bg" | "white-bg";
    bgColor: BgColorType;
    isPanelActive?: boolean;
}

const TopRowContent = ({
    testimonial,
    imagePosition,
    index,
    bgTheme,
    bgColor,
    isPanelActive
}: TopRowContentInterface) => {
    return (
        <div
            css={[
                rowWidthStyle,
                topRowStyle,
                isPanelActive && topRowActiveStyle
            ]}
            className="row-width top-row"
        >
            <div
                css={[
                    customerImagesSectionStyles,
                    customerImagesSectionStyleMap[`image-${imagePosition}`]
                ]}
                className="customer-images-section"
            >
                <div
                    css={[
                        customerImagesSectionImageStyles,
                        isPanelActive && customerImagesImgActiveStyle
                    ]}
                >
                    <Picture
                        image={{
                            src: testimonial.image,
                            alt: testimonial.title
                        }}
                    />
                </div>
            </div>
            <div
                css={[
                    customerQuotesSectionStyle,
                    customerQuotesSectionStyleMap[`image-${imagePosition}`]
                ]}
                className="customer-quotes-section"
            >
                <div
                    css={testimonialQuotesSectionStyle}
                    className="testimonial-quotes-section"
                >
                    <div
                        css={[
                            quoteContainerStyle,
                            isPanelActive && quoteContainerActiveStyle
                        ]}
                        className="quote-container"
                        key={index}
                    >
                        {testimonial.eyebrow && (
                            <p
                                css={[
                                    fontColorStyleMap[bgTheme],
                                    quoteContainerPStyle
                                ]}
                                className="eyebrow-md"
                            >
                                {testimonial.eyebrow}
                            </p>
                        )}
                        <blockquote
                            css={[
                                fontColorStyleMap[bgTheme],
                                quoteContainerPStyle
                            ]}
                            className="callout"
                        >
                            “{testimonial.quote}”
                        </blockquote>
                        <p css={quoteContainerPStyle}>
                            <span
                                css={fontColorStyleMap[bgTheme]}
                                className="eyebrow-lg"
                            >
                                {testimonial.name}
                            </span>
                            <br />
                            <span
                                css={fontColorStyleMap[bgTheme]}
                                className="body-md title"
                            >
                                {testimonial.title}
                            </span>
                        </p>
                        {testimonial.link && (
                            <Link
                                anchorProps={{
                                    tabIndex: isPanelActive ? 0 : -1
                                }}
                                text={testimonial.link.text}
                                href={testimonial.link.url}
                                type={testimonial.link.type ?? "primary"}
                                bgColor={
                                    bgColor === "blue" || bgColor === "black"
                                        ? "bgBlackBlue"
                                        : "bgWhite"
                                }
                                dataEventProps={testimonial.link.dataEventProps}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const topRowActiveStyle = css({
    opacity: 1,
    zIndex: 2
});
const customerImagesImgActiveStyle = css({
    // Picture component does not accept compiled CSS. Using nested selector to apply CSS to displayed image.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        animation: "fadein 1s ease-out 1 forwards"
    }
});

const quoteContainerActiveStyle = css({
    animation: "fadeswipe 1s 1",

    [media.below.sm]: {
        animation: "fadeswipesmall 1s 1"
    }
});

interface LogoTabContentProps {
    testimonialLogo: JSX.Element;
    isSelected?: boolean;
}

const LogoTabContent = ({
    testimonialLogo,
    isSelected
}: LogoTabContentProps) => {
    return (
        <>
            <span css={isSelected && topMarkerStyle} className="top-marker" />
            <span css={[logoButtonStyle]}>
                <span
                    css={[
                        logoImgStyle,
                        logoImgContainerStyle,
                        isSelected && logoImgSelectedStyle
                    ]}
                    className="logo-img-container"
                >
                    {testimonialLogo}
                </span>
            </span>
        </>
    );
};

// LogoTabContent styles

const logoImgStyle = css({
    "opacity": 0.5,
    "transition": "0.5s",
    ":hover": {
        opacity: 1
    }
});

const logoImgSelectedStyle = css({
    opacity: "1"
});

const logoButtonStyle = css({
    background: "none",
    border: 0,
    appearance: "none",
    paddingBlockStart: "20px",
    cursor: "pointer",
    position: "relative"
});

const topMarkerStyle = css({
    opacity: "1",
    position: "absolute",
    insetBlockStart: "-3px",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    height: "5px",
    borderRadius: "2px"
});

const logoImgContainerStyle = css({
    height: "34px",
    display: "flex",
    alignItems: "center"
});

// ADG4CustomerTestimonials styles

const headerStyles = css({
    textAlign: "center",
    marginBlockEnd: "40px"
});

const h2Styles = css({
    marginBlock: "40px 12px"
});

const pStyles = css({
    marginBlock: "0 12px"
});

const topLineStyles = css({
    position: "absolute",
    insetBlockStart: "1px",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    height: "1px",
    borderRadius: "2px",
    opacity: 0.5,

    [media.below.sm]: {
        insetBlockStart: "21px"
    }
});

const testimonialKeyframeStyles = css({
    "@keyframes fadeswipe": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0,
            transform: "translateX(100px)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },
    "@keyframes fadeswipesmall": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0,
            transform: "translateX(20px)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },

    "@keyframes fadein": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1
        }
    }
});

const rowWidthStyle = css({
    position: "relative",
    display: "flex",
    rowGap: token("space.250"),
    flexWrap: "wrap",
    alignItems: "stretch",

    [media.below.sm]: {
        flexDirection: "column",
        textAlign: "center"
    }
});

const topRowStyle = css({
    marginBlockEnd: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "32px",
    opacity: 0,
    zIndex: 1,

    [media.below.md]: {
        gridTemplateColumns: "1fr"
    }
});

const bgShapeStyle = css({
    position: "absolute",
    inset: 0,
    width: "95%",
    maxWidth: "1368px",
    margin: "auto",

    [media.below.sm]: {
        width: "100%"
    }
});

const customerImagesSectionStyles = css({
    position: "relative",
    flexBasis: "40%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",

    [media.only.sm]: {
        flexBasis: "50%"
    },

    [media.below.sm]: {
        width: "90%",
        margin: "0 auto"
    },
    [media.only.xs]: {
        width: "70%",
        margin: "0 auto"
    }
});

const customerImagesSectionImageStyles = css({
    // Picture component does not accept compiled CSS. Using nested selector to apply CSS to displayed image.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        height: "365px",

        [media.only.sm]: {
            height: "258px"
        },

        [media.only.xs]: {
            height: "258px"
        },

        [media.only.xxs]: {
            height: "210px"
        },

        width: "auto",
        display: "block"
    }
});

const customerQuotesSectionStyle = css({
    flexBasis: "60%",
    overflow: "hidden",

    [media.below.md]: {
        flexBasis: "50%"
    }
});

const testimonialQuotesSectionStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "end",
    borderRadius: "20px",
    position: "relative",
    margin: "0 auto",

    [media.above.md]: {
        paddingBlockStart: "80px"
    },

    [media.only.sm]: {
        width: "66%"
    },

    [media.below.sm]: {
        width: "100%"
    }
});

const quoteContainerStyle = css({
    transition: "opacity 1s",
    display: "flex",
    flexDirection: "column",
    gap: "24px"
});

const quoteContainerPStyle = css({
    margin: "0"
});

const logosContainerBaseStyle = css({
    "overflow": "hidden",
    "width": "100%",
    "justifyContent": "space-between",

    "@media (max-width: 1140px)": {
        flexDirection: "column"
    },

    [media.below.sm]: {
        marginBlockStart: "20px"
    }
});

const logosStyle = css({
    // !important used to override Adg4DraggableTabList styles
    marginBlockEnd: "40px !important",
    paddingBlockStart: "1px !important",
    listStyle: "none !important"
});

const logoOptionStyle = css({
    "display": "flex",
    "justifyContent": "stretch",
    "alignItems": "stretch",
    "position": "relative",

    "&[aria-selected=true]": {
        paddingBlockStart: "0"
    }
});

const testimonialStyles = css({
    position: "relative",
    zIndex: 1,
    marginBlockEnd: token("space.500")
});

const tabPanelWrapperStyles = css({
    "display": "grid",
    "gridTemplateColumns": "1fr",
    "gridTemplateRows": "1fr",

    // active panel style overrides nested here in order to expand
    // the container to the height of largest child.
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-panel": {
        "opacity": 0,
        "zIndex": 1,
        "display": "flex",
        "gridArea": "1 / 1",

        "&.active-panel": {
            opacity: 1,
            zIndex: 2
        }
    }
});
